import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Radio,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { bulkSaveRates, getRatesData } from '../../services/api';
import useChannelMappingRows from '../../hooks/useChannelMappingRows';
import RateUpdateProgressPanel from './RateUpdateProgressPanel';
import {
  inventoryDrawerFooterSx,
  inventoryDrawerHeaderSx,
} from '../../utils/inventoryMobileLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import { compareRatePlans } from '../../utils/ratePlanSort';
import RateActionSuccessDialog, { RATE_SUCCESS_COPY } from './RateActionSuccessDialog';
import RateDayMultiplierCards, {
  isValidDayMultiplier,
  parseDayMultiplier,
} from './RateDayMultiplierCards';

const THEME_COLOR = '#4ce1b6';
const MAX_RANGE_DAYS = 90;

const DAY_OPTIONS = [
  { value: 0, label: 'Sun', shortLabel: 'S', color: '#FF6B6B' },
  { value: 1, label: 'Mon', shortLabel: 'M', color: '#4ECDC4' },
  { value: 2, label: 'Tue', shortLabel: 'T', color: '#45B7D1' },
  { value: 3, label: 'Wed', shortLabel: 'W', color: '#FFA07A' },
  { value: 4, label: 'Thu', shortLabel: 'T', color: '#98D8C8' },
  { value: 5, label: 'Fri', shortLabel: 'F', color: '#F7DC6F' },
  { value: 6, label: 'Sat', shortLabel: 'S', color: '#BB8FCE' },
];

const WEEKDAY_VALUES = [1, 2, 3, 4, 5];
const WEEKEND_VALUES = [0, 6];

const rowKey = (platform, roomTypeId, ratePlanId) => `${platform}:${roomTypeId}:${ratePlanId}`;

const listingDayRate = (listings, roomTypeId, ratePlanId, dateStr) => {
  const room = (listings || []).find((item) => String(item.id) === String(roomTypeId));
  const plans = room?.rate_plans || {};
  const plan =
    plans[ratePlanId]
    || plans[String(ratePlanId)]
    || Object.values(plans).find(
      (item) => Number(item?.rate_plan_id ?? item?.id) === Number(ratePlanId)
    );
  const day = plan?.rates?.[dateStr];
  if (!day) return null;
  const value = day.base_rate ?? day.final_rate;
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
};

const listingDayRateFromChannels = (channelListings, channels, roomTypeId, ratePlanId, dateStr) => {
  const listingsByChannel = channelListings && typeof channelListings === 'object' ? channelListings : {};
  const orderedIds = (Array.isArray(channels) ? channels : [])
    .map((channel) => String(channel?.id ?? ''))
    .filter((id) => id && id !== 'pms' && id !== 'all');
  const extraIds = Object.keys(listingsByChannel).filter(
    (id) => id && id !== 'pms' && !orderedIds.includes(id)
  );
  for (const channelId of [...orderedIds, ...extraIds]) {
    const value = listingDayRate(listingsByChannel[channelId], roomTypeId, ratePlanId, dateStr);
    if (value != null) return value;
  }
  return null;
};

const OCCUPANCY_GUESTS = {
  s: 1, d: 2, t: 3, q: 4, p: 5, h: 6, hp: 7, o: 8,
  n: 9, de: 10, el: 11, tw: 12, th: 13, fo: 14,
};

const occupancyGuests = (plan) => {
  const code = String(plan?.code || '').trim().toLowerCase();
  const parts = code.split('-').filter(Boolean);
  if (parts.length >= 2 && OCCUPANCY_GUESTS[parts[parts.length - 2]] != null) {
    return OCCUPANCY_GUESTS[parts[parts.length - 2]];
  }
  const name = String(plan?.name || '').toLowerCase();
  const words = [
    ['hept', 7],
    ['hex', 6],
    ['oct', 8],
    ['pent', 5],
    ['quad', 4],
    ['triple', 3],
    ['single', 1],
    ['double', 2],
  ];
  for (const [word, guests] of words) {
    if (name.includes(word)) return guests;
  }
  return null;
};

const planAppliesToRoom = (plan, room) => {
  const appliesAll = plan?.applies_to_all_room_types !== false;
  const ids = Array.isArray(plan?.room_type_ids) ? plan.room_type_ids : [];
  if (!appliesAll && ids.length > 0) {
    if (!ids.some((id) => Number(id) === Number(room.id))) return false;
  }
  const cap = Number(room.max_guests || room.capacity || 0);
  const guests = occupancyGuests(plan);
  if (guests != null && cap > 0 && guests > cap) return false;
  return true;
};

const isOccupancyCmPlan = (plan) => occupancyGuests(plan) != null;

const buildMappedCmRows = (pairs) =>
  (Array.isArray(pairs) ? pairs : []).map((pair) => ({
    ...pair,
    key: rowKey('cm', pair.roomTypeId, pair.ratePlanId),
    platform: 'cm',
    label: `${pair.roomTypeName} · ${pair.ratePlanName}${
      pair.mealPlan ? ` (${pair.mealPlan})` : ''
    }`,
  }));

const buildPlanRows = (rooms, plans, platform) => {
  const rows = [];
  rooms.forEach((room) => {
    plans.forEach((plan) => {
      if (platform === 'cm' && !isOccupancyCmPlan(plan) && (plan.is_pms_enabled || plan.enablePMS)) {
        return;
      }
      if (!planAppliesToRoom(plan, room)) return;
      const key = rowKey(platform, room.id, plan.id);
      rows.push({
        key,
        platform,
        roomTypeId: room.id,
        roomTypeName: room.name || `Room ${room.id}`,
        ratePlanId: plan.id,
        ratePlanName: plan.name || `Plan ${plan.id}`,
        ratePlanCode: plan.code || '',
        mealPlan: plan.meal_plan || '',
        sort_order: plan.sort_order ?? 0,
        label: `${room.name || room.id} · ${plan.name || plan.id}${
          plan.meal_plan ? ` (${plan.meal_plan})` : ''
        }`,
      });
    });
  });
  return rows;
};

const roundToTwoDecimals = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  return parseFloat((Math.round(Number(value) * 100) / 100).toFixed(2));
};

const isInvalidRate = (value) => {
  if (value === '' || value === null || value === undefined) return true;
  const number = Number(value);
  return Number.isNaN(number) || number < 0;
};

const RATE_FIELD_ERROR = 'Required';

const eachDateStr = (start, end) => {
  const dates = [];
  const cursor = moment(start).startOf('day');
  const last = moment(end).startOf('day');
  while (cursor.isSameOrBefore(last, 'day')) {
    dates.push(cursor.format('YYYY-MM-DD'));
    cursor.add(1, 'day');
  }
  return dates;
};

const medianNumber = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const snapWeekendRatio = (weekdayRate, weekendRate) => {
  if (!(weekdayRate > 0) || !(weekendRate > weekdayRate)) return null;
  const snapped = Math.round((weekendRate / weekdayRate) / 0.05) * 0.05;
  if (snapped <= 1) return null;
  return Math.min(2, Number(snapped.toFixed(2)));
};

/**
 * Group Update drawer: PMS meal plans plus every CM occupancy plan.
 * PMS chip saves local base rates. Channel chip writes ChannelRate rows and pushes OTAs.
 */
export default function GroupRateUpdateDrawer({
  open,
  onClose,
  roomTypes = [],
  ratePlans = [],
  channelManagerRatePlans: _channelManagerRatePlans = [],
  channels = [],
  isDark,
  themeColors,
  onSaved,
}) {
  const { mappingPairs, mappingsLoading } = useChannelMappingRows(open);
  const mappedCmRows = useMemo(() => {
    const rows = buildMappedCmRows(mappingPairs);
    const rooms = Array.isArray(roomTypes) ? roomTypes : [];
    if (!rooms.length) return rows;
    const allowed = new Set(
      rooms.map((room) => Number(room.id)).filter((id) => Number.isFinite(id) && id > 0)
    );
    return rows.filter((row) => allowed.has(Number(row.roomTypeId)));
  }, [mappingPairs, roomTypes]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [daySelectionType, setDaySelectionType] = useState('custom'); // 'custom' | 'weekdays' | 'weekend'
  const [selectedDays, setSelectedDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [ratePlanFilter, setRatePlanFilter] = useState('channel_manager');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [ratesByKey, setRatesByKey] = useState({});
  const [bulkFillValue, setBulkFillValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState({ phase: 'idle', total: 0, result: null });
  const [errorMessage, setErrorMessage] = useState(null);
  const [successDialog, setSuccessDialog] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [bulkFillError, setBulkFillError] = useState('');
  const [dateError, setDateError] = useState('');
  const [weekdayMultiplier, setWeekdayMultiplier] = useState('1.00');
  const [weekendMultiplier, setWeekendMultiplier] = useState('1.00');
  const weekendMultiplierTouchedRef = useRef(false);
  const drawerRootRef = useRef(null);
  const rateInputRefs = useRef({});
  const bulkFillInputRef = useRef(null);
  const toDateInputRef = useRef(null);
  const lastAutofillFromRef = useRef('');
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface]
  );

  const planRows = useMemo(() => {
    const rooms = Array.isArray(roomTypes) ? roomTypes : [];
    const pmsPlans = Array.isArray(ratePlans) ? ratePlans : [];
    return [
      ...buildPlanRows(rooms, pmsPlans, 'pms'),
      ...mappedCmRows,
    ];
  }, [roomTypes, ratePlans, mappedCmRows]);

  const filteredPlanRows = useMemo(
    () =>
      planRows.filter((row) =>
        ratePlanFilter === 'channel_manager' ? row.platform === 'cm' : row.platform === 'pms'
      ),
    [planRows, ratePlanFilter]
  );

  const rowsByRoom = useMemo(() => {
    const map = new Map();
    filteredPlanRows.forEach((row) => {
      if (!map.has(row.roomTypeId)) {
        map.set(row.roomTypeId, { name: row.roomTypeName, rows: [] });
      }
      map.get(row.roomTypeId).rows.push(row);
    });
    map.forEach((group) => {
      group.rows.sort(compareRatePlans);
    });
    return map;
  }, [filteredPlanRows]);

  const resetForm = useCallback(() => {
    const tomorrow = moment().add(1, 'day');
    const dayAfter = moment().add(2, 'day');
    setDateRange([tomorrow, dayAfter]);
    setDaySelectionType('custom');
    setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
    setRatePlanFilter('channel_manager');
    setBulkFillValue('');
    setErrorMessage(null);
    setSuccessDialog(null);
    setFieldErrors({});
    setBulkFillError('');
    setDateError('');
    weekendMultiplierTouchedRef.current = false;
    setWeekdayMultiplier('1.00');
    setWeekendMultiplier('1.00');
    setProgress({ phase: 'idle', total: 0, result: null });
    const visibleKeys = new Set(
      planRows
        .filter((row) => row.platform === 'cm')
        .map((row) => row.key)
    );
    setSelectedKeys(visibleKeys);
    const emptyRates = {};
    planRows.forEach((r) => {
      emptyRates[r.key] = '';
    });
    setRatesByKey(emptyRates);
  }, [planRows]);

  useEffect(() => {
    if (open) resetForm();
    // Reset only when the drawer opens, not when catalogs refresh after save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || filteredPlanRows.length === 0) return;
    setSelectedKeys(new Set(filteredPlanRows.map((row) => row.key)));
  }, [open, filteredPlanRows]);

  const selectedVisibleCount = filteredPlanRows.filter((row) => selectedKeys.has(row.key)).length;
  const allSelected = filteredPlanRows.length > 0 && selectedVisibleCount === filteredPlanRows.length;
  const someSelected = selectedVisibleCount > 0 && selectedVisibleCount < filteredPlanRows.length;
  const toggleSelectAll = () => {
    setSelectedKeys(new Set(filteredPlanRows.map((r) => r.key)));
  };

  const setPlatformFilter = (value) => {
    setRatePlanFilter(value);
    const nextRows = planRows.filter((row) =>
      value === 'channel_manager' ? row.platform === 'cm' : row.platform === 'pms'
    );
    setSelectedKeys(new Set(nextRows.map((row) => row.key)));
    setFieldErrors({});
    setBulkFillError('');
  };

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const focusRateInput = (key) => {
    window.requestAnimationFrame(() => {
      const node = rateInputRefs.current[key];
      if (!node) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      node.focus({ preventScroll: true });
      if (typeof node.select === 'function') node.select();
    });
  };

  const rangeDayCount = () => {
    if (!dateRange[0] || !dateRange[1]) return 0;
    return moment(dateRange[1]).startOf('day').diff(moment(dateRange[0]).startOf('day'), 'days') + 1;
  };

  const maxToDate = (from) => (from ? from.clone().add(MAX_RANGE_DAYS - 1, 'days') : null);

  const focusToDate = () => {
    window.requestAnimationFrame(() => {
      const node = toDateInputRef.current;
      if (!node) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      node.focus({ preventScroll: true });
    });
  };

  const fromDateStr = dateRange[0] ? moment(dateRange[0]).format('YYYY-MM-DD') : '';
  const toDateStr = dateRange[1] ? moment(dateRange[1]).format('YYYY-MM-DD') : '';

  useEffect(() => {
    if (!open) {
      lastAutofillFromRef.current = '';
      return undefined;
    }
    if (!fromDateStr || planRows.length === 0) return undefined;
    const fromChanged = lastAutofillFromRef.current !== fromDateStr;
    if (!fromChanged && lastAutofillFromRef.current) return undefined;
    lastAutofillFromRef.current = fromDateStr;
    let cancelled = false;
    const loadFromDateRates = async () => {
      try {
        const detectEnd = moment.min(
          moment(fromDateStr).add(13, 'days'),
          moment.max(moment(toDateStr || fromDateStr), moment(fromDateStr).add(6, 'days'))
        );
        const detectEndStr = detectEnd.format('YYYY-MM-DD');
        const lookupDates = eachDateStr(fromDateStr, detectEndStr);
        const data = await getRatesData(fromDateStr, detectEndStr, 'all');
        if (cancelled) return;
        const channelListings = data?.channel_listings || {};
        const pmsListings = data?.listings || channelListings.pms || [];
        const rateFor = (row, dateStr) => (
          row.platform === 'pms'
            ? listingDayRate(pmsListings, row.roomTypeId, row.ratePlanId, dateStr)
            : listingDayRateFromChannels(
              channelListings,
              channels,
              row.roomTypeId,
              row.ratePlanId,
              dateStr
            )
        );
        const fromIsWeekend = WEEKEND_VALUES.includes(moment(fromDateStr).day());
        const nextRates = {};
        const ratios = [];
        planRows.forEach((row) => {
          const weekdayRates = [];
          const weekendRates = [];
          lookupDates.forEach((dateStr) => {
            const value = rateFor(row, dateStr);
            if (value == null || value <= 0) return;
            if (WEEKEND_VALUES.includes(moment(dateStr).day())) weekendRates.push(value);
            else weekdayRates.push(value);
          });
          const weekdayMedian = medianNumber(weekdayRates);
          const weekendMedian = medianNumber(weekendRates);
          const ratio = snapWeekendRatio(weekdayMedian, weekendMedian);
          if (ratio != null) ratios.push(ratio);
          const fromValue = rateFor(row, fromDateStr);
          const value = fromIsWeekend && weekdayMedian != null ? weekdayMedian : fromValue;
          nextRates[row.key] = value != null ? String(roundToTwoDecimals(value)) : '';
        });
        setRatesByKey(nextRates);
        if (!weekendMultiplierTouchedRef.current) {
          const detected = medianNumber(ratios);
          setWeekendMultiplier(detected != null ? detected.toFixed(2) : '1.00');
        }
      } catch {
        // Keep whatever the form already has if the lookup fails.
      }
    };
    loadFromDateRates();
    return () => {
      cancelled = true;
    };
  }, [open, fromDateStr, toDateStr, planRows, channels]);

  const toggleRow = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    clearFieldError(key);
  };

  const selectDayType = (type) => {
    setDaySelectionType(type);
    if (type === 'weekdays') setSelectedDays([...WEEKDAY_VALUES]);
    else if (type === 'weekend') setSelectedDays([...WEEKEND_VALUES]);
    else setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        const next = prev.filter((d) => d !== day);
        return next.length ? next : prev; // keep at least one day
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  const applyBulkFill = () => {
    const value = bulkFillValue.trim();
    if (isInvalidRate(value)) {
      setBulkFillError(RATE_FIELD_ERROR);
      setErrorMessage(null);
      window.requestAnimationFrame(() => {
        bulkFillInputRef.current?.focus();
        bulkFillInputRef.current?.select?.();
      });
      return;
    }
    if (selectedVisibleCount === 0) {
      setErrorMessage('Select at least one rate plan row');
      return;
    }
    setErrorMessage(null);
    setBulkFillError('');
    setFieldErrors({});
    setRatesByKey((prev) => {
      const next = { ...prev };
      filteredPlanRows.forEach((row) => {
        if (selectedKeys.has(row.key)) {
          next[row.key] = String(roundToTwoDecimals(value));
        }
      });
      return next;
    });
  };

  const collectDates = () => {
    if (!dateRange[0] || !dateRange[1]) return [];
    const dates = [];
    const start = moment(dateRange[0]).startOf('day');
    const end = moment(dateRange[1]).startOf('day');
    let current = start.clone();
    while (current.isSameOrBefore(end, 'day')) {
      if (selectedDays.includes(current.day())) {
        dates.push(current.format('YYYY-MM-DD'));
      }
      current.add(1, 'day');
    }
    return dates;
  };

  const handleSave = async () => {
    setErrorMessage(null);
    setSuccessDialog(null);
    setBulkFillError('');
    setFieldErrors({});
    setDateError('');

    if (!dateRange[0] || !dateRange[1]) {
      setDateError('Select a date range');
      focusToDate();
      return;
    }
    if (dateRange[0].isBefore(moment().startOf('day'), 'day')) {
      setDateError('Cannot set rates for past dates');
      focusToDate();
      return;
    }
    if (rangeDayCount() > MAX_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_RANGE_DAYS} days`);
      focusToDate();
      return;
    }
    if (selectedVisibleCount === 0) {
      setErrorMessage('Select at least one room type / meal plan');
      return;
    }

    const dates = collectDates();
    if (dates.length === 0) {
      setErrorMessage('No dates match the selected weekdays in this range');
      return;
    }

    const selectedRows = filteredPlanRows.filter((r) => selectedKeys.has(r.key));
    const invalidRows = selectedRows.filter((r) => isInvalidRate(ratesByKey[r.key]));
    if (invalidRows.length) {
      const nextErrors = {};
      invalidRows.forEach((row) => {
        nextErrors[row.key] = RATE_FIELD_ERROR;
      });
      setFieldErrors(nextErrors);
      setErrorMessage(
        invalidRows.length === 1
          ? `Enter a valid rate for “${invalidRows[0].label}”`
          : `Enter a valid rate for ${invalidRows.length} highlighted plans`
      );
      focusRateInput(invalidRows[0].key);
      return;
    }

    if (!isValidDayMultiplier(weekdayMultiplier) || !isValidDayMultiplier(weekendMultiplier)) {
      setErrorMessage('Enter a weekday and weekend multiplier greater than 0');
      return;
    }
    const weekdayFactor = parseDayMultiplier(weekdayMultiplier);
    const weekendFactor = parseDayMultiplier(weekendMultiplier);

    const isPmsSave = ratePlanFilter === 'pms';
    const otaChannels = (Array.isArray(channels) ? channels : [])
      .map((channel) => String(channel.id))
      .filter(Boolean);
    if (!isPmsSave && otaChannels.length === 0) {
      setErrorMessage('No OTA channels configured to update');
      return;
    }

    const compactItems = [];
    selectedRows.forEach((row) => {
      const base = roundToTwoDecimals(ratesByKey[row.key]);
      const item = {
        room_type_id: row.roomTypeId,
        rate_plan_id: row.ratePlanId,
        base_rate: base,
        extra_adult_rate: 0,
        extra_child_rate: 0,
        available: true,
        currency: 'INR',
      };
      if (isPmsSave) {
        compactItems.push({ ...item, channel_id: 'pms' });
        return;
      }
      otaChannels.forEach((channelId) => {
        compactItems.push({ ...item, channel_id: channelId });
      });
    });

    if (compactItems.length === 0) {
      setErrorMessage('Nothing to save');
      return;
    }

    const estimatedTotal = dates.length * compactItems.length;
    if (estimatedTotal > 50000) {
      setErrorMessage(
        `Too many updates (${estimatedTotal}). Narrow the date range or fewer plans.`
      );
      return;
    }

    setIsSaving(true);
    setProgress({ phase: 'saving', total: estimatedTotal, result: null });
    try {
      const result = await bulkSaveRates(null, {
        persistAfterPush: !isPmsSave,
        compact: {
          dates,
          weekdayFactor,
          weekendFactor,
          items: compactItems,
        },
      });
      const failed = result?.failed ?? 0;
      const successful = result?.successful ?? estimatedTotal;
      const otaFailed = Boolean(
        !isPmsSave && result?.ota?.attempted && result?.ota?.success === false
      );

      if ((failed > 0 && successful === 0) || otaFailed || result?.success === false) {
        setProgress({ phase: 'done', total: estimatedTotal, result });
        const firstError =
          result?.error
          || result?.ota?.error
          || (result?.results || []).find((r) => !r.success)?.error
          || 'All updates failed';
        const friendly =
          String(firstError).includes('ConversionSyntax') || String(firstError).includes('decimal')
            ? 'Invalid rate value. Enter numeric rates only (e.g. 2400).'
            : firstError;
        setErrorMessage(friendly);
        return;
      }

      setProgress({ phase: 'done', total: estimatedTotal, result });
      setSuccessDialog(isPmsSave ? RATE_SUCCESS_COPY.groupPms : RATE_SUCCESS_COPY.groupChannel);
      if (typeof onSaved === 'function') {
        window.setTimeout(() => {
          Promise.resolve(onSaved()).catch(() => {});
        }, isPmsSave ? 0 : 2000);
      }
    } catch (error) {
      setProgress({ phase: 'idle', total: 0, result: null });
      setErrorMessage(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Group update failed. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const chipSx = (filled) => ({
    cursor: 'pointer',
    fontWeight: filled ? 700 : 500,
    backgroundColor: filled ? THEME_COLOR : 'transparent',
    color: filled ? '#000' : 'inherit',
    borderColor: filled ? THEME_COLOR : alpha(THEME_COLOR, 0.45),
    borderWidth: filled ? 1.5 : 1,
    '&:hover': {
      backgroundColor: filled ? THEME_COLOR : alpha(THEME_COLOR, 0.08),
      borderColor: THEME_COLOR,
    },
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !isSaving && onClose()}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 640 },
          overflow: 'hidden',
          backgroundColor: isDark ? themeColors.surface : '#ffffff',
        },
      }}
    >
      <Box
        ref={drawerRootRef}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...inventoryDrawerHeaderSx(isDark),
            position: 'relative',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600, lineHeight: 1.2 }}>
              Group Update
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PMS saves local rates. Channel pushes OTAs first, then saves rates.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isSaving}
            sx={{
              color: themeColors.subtleText,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {isSaving && (
            <LinearProgress
              sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2 }}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 2.5 }}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <LocalizationProvider dateAdapter={AdapterMoment}>
            {/* Date range */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Date range
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <DatePicker
                  label="From"
                  value={dateRange[0]}
                  minDate={moment().startOf('day')}
                  onChange={(v) => {
                    if (!v) return;
                    const newFrom = v.isBefore(moment().startOf('day'), 'day')
                      ? moment().startOf('day')
                      : v;
                    const maxTo = maxToDate(newFrom);
                    let newTo = newFrom.clone().add(1, 'day');
                    if (maxTo && newTo.isAfter(maxTo)) newTo = maxTo;
                    setDateRange([newFrom, newTo]);
                    setDateError('');
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
                <DatePicker
                  label="To"
                  value={dateRange[1]}
                  minDate={dateRange[0]?.clone().add(1, 'day')}
                  maxDate={maxToDate(dateRange[0])}
                  onChange={(v) => {
                    if (v && dateRange[0] && v.isAfter(dateRange[0])) {
                      const maxTo = maxToDate(dateRange[0]);
                      setDateRange([dateRange[0], maxTo && v.isAfter(maxTo) ? maxTo : v]);
                      setDateError('');
                    }
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: Boolean(dateError),
                      helperText: dateError || undefined,
                      inputRef: toDateInputRef,
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Maximum {MAX_RANGE_DAYS} days per update. Rates autofill from the From date.
              </Typography>
            </Box>

            {/* Day Selection — Custom / Weekdays / Weekend (same as Update drawer) */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Day Selection
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pl: 1 }}>
                {[
                  { value: 'custom', label: 'Custom' },
                  { value: 'weekdays', label: 'Weekdays' },
                  { value: 'weekend', label: 'Weekend' },
                ].map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Radio
                        size="small"
                        checked={daySelectionType === opt.value}
                        onChange={() => selectDayType(opt.value)}
                        sx={{
                          color: alpha(THEME_COLOR, 0.6),
                          '&.Mui-checked': { color: THEME_COLOR },
                        }}
                      />
                    }
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
              </Box>
              {daySelectionType === 'custom' && (
                <Box sx={{ mt: 1.5, pl: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {DAY_OPTIONS.map((day) => {
                      const isSelected = selectedDays.includes(day.value);
                      return (
                        <Chip
                          key={day.value}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {day.shortLabel}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                {day.label}
                              </Typography>
                            </Box>
                          }
                          onClick={() => toggleDay(day.value)}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: isSelected ? 600 : 400,
                            backgroundColor: isSelected ? day.color : 'transparent',
                            color: isSelected ? '#fff' : themeColors.subtleText,
                            borderColor: isSelected ? day.color : themeColors.surfaceBorder,
                            borderWidth: isSelected ? 2 : 1,
                            borderStyle: 'solid',
                            height: 36,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: isSelected ? day.color : alpha(day.color, 0.1),
                              borderColor: day.color,
                              transform: 'scale(1.05)',
                              boxShadow: isSelected
                                ? `0 2px 8px ${alpha(day.color, 0.4)}`
                                : `0 2px 4px ${alpha(day.color, 0.2)}`,
                            },
                            '&:active': { transform: 'scale(0.95)' },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>

            <RateDayMultiplierCards
              weekdayValue={weekdayMultiplier}
              weekendValue={weekendMultiplier}
              onWeekdayChange={setWeekdayMultiplier}
              onWeekendChange={(value) => {
                weekendMultiplierTouchedRef.current = true;
                setWeekendMultiplier(value);
              }}
              daySelectionType={daySelectionType}
              isDark={isDark}
              platform={ratePlanFilter}
            />

            {/* Bulk fill */}
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1.5,
                border: `1px solid ${alpha(THEME_COLOR, 0.35)}`,
                background: isDark
                  ? `linear-gradient(135deg, ${alpha(THEME_COLOR, 0.08)} 0%, transparent 70%)`
                  : alpha(THEME_COLOR, 0.06),
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Quick fill selected
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Rate"
                  value={bulkFillValue}
                  error={Boolean(bulkFillError)}
                  helperText={bulkFillError || undefined}
                  inputRef={bulkFillInputRef}
                  onChange={(e) => {
                    setBulkFillValue(e.target.value);
                    if (bulkFillError) setBulkFillError('');
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={{ flex: 1 }}
                  inputProps={{ min: 0, step: '0.01' }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={applyBulkFill}
                  disabled={isSaving}
                  sx={{
                    borderColor: THEME_COLOR,
                    color: THEME_COLOR,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: THEME_COLOR, backgroundColor: alpha(THEME_COLOR, 0.08) },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>

            {/* Rate plan matrix */}
            <Box
              sx={{
                mb: 2.5,
                borderRadius: 1.5,
                border: `1px solid ${themeColors.surfaceBorder}`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr auto',
                  gap: 1,
                  px: 1.25,
                  py: 1,
                  alignItems: 'center',
                  backgroundColor: isDark ? alpha(THEME_COLOR, 0.12) : alpha(THEME_COLOR, 0.1),
                  borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                }}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleSelectAll}
                  sx={{
                    p: 0.5,
                    color: alpha(THEME_COLOR, 0.6),
                    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: THEME_COLOR },
                  }}
                />
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
                    RATEPLANS ({selectedVisibleCount}/{filteredPlanRows.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {daySelectionType === 'weekdays'
                      ? `Weekdays ×${parseDayMultiplier(weekdayMultiplier).toFixed(2)}`
                      : daySelectionType === 'weekend'
                        ? `Weekend ×${parseDayMultiplier(weekendMultiplier).toFixed(2)}`
                        : `Weekdays ×${parseDayMultiplier(weekdayMultiplier).toFixed(2)} · Weekend ×${parseDayMultiplier(weekendMultiplier).toFixed(2)}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {[
                    { value: 'pms', label: 'PMS' },
                    { value: 'channel_manager', label: 'Channel' },
                  ].map((opt) => (
                    <Chip
                      key={opt.value}
                      size="small"
                      label={opt.label}
                      onClick={() => setPlatformFilter(opt.value)}
                      variant="outlined"
                      sx={{
                        ...chipSx(ratePlanFilter === opt.value),
                        height: 24,
                        fontSize: '0.7rem',
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {filteredPlanRows.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {ratePlanFilter === 'channel_manager'
                      ? mappingsLoading
                        ? 'Loading mapped Channel Manager rate plans…'
                        : 'No mapped Channel Manager room types and rate plans. Save channel mappings first.'
                      : 'No PMS meal plans available.'}
                  </Typography>
                </Box>
              ) : (
                Array.from(rowsByRoom.entries()).map(([roomId, group]) => (
                  <Box key={roomId}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                        borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: THEME_COLOR, textTransform: 'uppercase' }}
                      >
                        {group.name}
                      </Typography>
                    </Box>
                    {group.rows.map((row) => {
                      const checked = selectedKeys.has(row.key);
                      const fieldError = fieldErrors[row.key];
                      return (
                        <Box
                          key={row.key}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '40px 1fr 132px',
                            gap: 1,
                            px: 1.25,
                            py: 0.75,
                            alignItems: fieldError ? 'flex-start' : 'center',
                            borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                            opacity: checked ? 1 : 0.55,
                            '&:hover': {
                              backgroundColor: isDark
                                ? 'rgba(76, 225, 182, 0.04)'
                                : 'rgba(76, 225, 182, 0.04)',
                            },
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => toggleRow(row.key)}
                            sx={{
                              p: 0.5,
                              color: alpha(THEME_COLOR, 0.6),
                              '&.Mui-checked': { color: THEME_COLOR },
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', pr: 0.5 }}>
                            {row.ratePlanName}
                            {row.mealPlan ? (
                              <Box component="span" sx={{ color: 'text.secondary' }}>
                                {' '}
                                ({row.mealPlan})
                              </Box>
                            ) : null}
                            {row.platform === 'cm' && row.ratePlanCode ? (
                              <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                                {row.ratePlanCode}
                              </Box>
                            ) : null}
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={ratesByKey[row.key] ?? ''}
                            disabled={!checked || isSaving}
                            error={Boolean(fieldError)}
                            helperText={fieldError || undefined}
                            inputRef={(node) => {
                              if (node) rateInputRefs.current[row.key] = node;
                              else delete rateInputRefs.current[row.key];
                            }}
                            onChange={(e) => {
                              setRatesByKey((prev) => ({
                                ...prev,
                                [row.key]: e.target.value,
                              }));
                              clearFieldError(row.key);
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">₹</InputAdornment>
                              ),
                            }}
                            inputProps={{ min: 0, step: '0.01' }}
                            sx={{
                              '& .MuiInputBase-root': { height: 36 },
                              '& .MuiFormHelperText-root': {
                                mx: 0,
                                mt: 0.25,
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ))
              )}
            </Box>
          </LocalizationProvider>
        </Box>

        <Box sx={{ px: 2 }}>
          <RateUpdateProgressPanel
            phase={progress.phase}
            total={progress.total}
            result={progress.result}
            pmsOnly={ratePlanFilter === 'pms'}
          />
        </Box>

        <Box
          sx={{
            p: 1.5,
            ...inventoryDrawerFooterSx(isDark),
            display: 'flex',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={isSaving}
            sx={{
              borderColor: themeColors.surfaceBorder,
              color: themeColors.subtleText,
              '&:hover': {
                borderColor: THEME_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                color: THEME_COLOR,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSave}
            disabled={isSaving || filteredPlanRows.length === 0}
            sx={{
              backgroundColor: THEME_COLOR,
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: THEME_COLOR,
                opacity: 0.9,
              },
              '&:disabled': {
                backgroundColor: themeColors.surfaceBorder,
                color: isDark ? '#999' : '#666',
              },
            }}
          >
            {isSaving ? 'Saving…' : progress.phase === 'done' ? 'Done' : 'Save Group Rates'}
          </Button>
        </Box>
        <RateActionSuccessDialog
          open={Boolean(successDialog)}
          title={successDialog?.title}
          message={successDialog?.message}
          container={() => drawerRootRef.current || undefined}
          onClose={() => {
            setSuccessDialog(null);
            onClose();
          }}
        />
      </Box>
    </Drawer>
  );
}
